import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import QuoteDetailsPanel from '../public-service-tracking/components/QuoteDetailsPanel';
import { supabase, supabaseAdmin } from '../../lib/supabase';
import { decodeQuoteAccessToken } from '../../utils/sendQuoteEmail';
import { buildQuoteAcceptBookingDraft } from '../../utils/quoteAcceptBooking';

const PublicQuotePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [appointment, setAppointment] = useState(null);
  const [quote, setQuote] = useState(null);
  const [tokenClaims, setTokenClaims] = useState(null);
  const [sessionUser, setSessionUser] = useState(null);

  const loadQuote = async () => {
    if (!token) {
      setError('Lien de devis invalide (token manquant).');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data: authData } = await supabase.auth.getUser();
      setSessionUser(authData?.user || null);

      const payload = decodeQuoteAccessToken(token);
      setTokenClaims({
        quoteId: payload.q,
        appointmentId: payload.a,
        email: payload.e,
        clientId: payload.c,
        exp: payload.exp,
      });

      // Affichage depuis le token (pas d'edge function)
      if (payload.s?.quote && payload.s?.appointment) {
        setQuote(payload.s.quote);
        setAppointment(payload.s.appointment);
      } else {
        throw new Error(
          'Lien incomplet. Demandez un nouvel envoi du devis par email.'
        );
      }

      // Rafraîchir le statut en base si possible (sans edge), sinon garder le snapshot
      try {
        const client = supabaseAdmin || supabase;
        const { data: freshQuote } = await client
          .from('appointment_quote')
          .select('*')
          .eq('id', payload.q)
          .eq('appointment_id', payload.a)
          .maybeSingle();

        if (freshQuote) {
          setQuote(freshQuote);
        }
      } catch (refreshErr) {
        console.warn('Refresh devis ignoré:', refreshErr);
      }
    } catch (err) {
      console.error('Erreur chargement devis public:', err);
      setError(err?.message || 'Impossible de charger ce devis.');
      setAppointment(null);
      setQuote(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleQuoteDecision = async (nextStatus, refusalPayload = null) => {
    if (!tokenClaims?.quoteId) {
      throw new Error('Devis introuvable');
    }

    const updateData = {
      status: nextStatus,
      updated_at: new Date().toISOString(),
    };

    if (nextStatus === 'rejected' && refusalPayload) {
      updateData.refusal_reason = refusalPayload.refusal_reason || null;
      updateData.refusal_comment = refusalPayload.refusal_comment || null;
      updateData.refused_at = refusalPayload.refused_at || new Date().toISOString();
    }

    const client = supabaseAdmin || supabase;
    const { data, error: updateError } = await client
      .from('appointment_quote')
      .update(updateData)
      .eq('id', tokenClaims.quoteId)
      .select('*')
      .single();

    if (updateError) throw updateError;
    setQuote(data);
  };

  const handleStartAcceptBooking = () => {
    if (!appointment || !quote) return;

    const bookingDraft = buildQuoteAcceptBookingDraft(appointment, quote);
    if (!bookingDraft) return;

    if (tokenClaims?.email && !bookingDraft.email) {
      bookingDraft.email = tokenClaims.email;
    }

    navigate('/quote-accept-booking', {
      state: { bookingData: bookingDraft },
    });
  };

  const identityHint = sessionUser
    ? `Connecté : ${sessionUser.email || sessionUser.id}`
    : `Accès invité via lien sécurisé${
        tokenClaims?.email ? ` (${tokenClaims.email})` : ''
      }`;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Chargement de votre devis…</p>
        </div>
      </div>
    );
  }

  if (error || !appointment || !quote) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full p-6 bg-card rounded-lg border border-border text-center">
          <Icon name="AlertCircle" size={40} className="text-error mx-auto mb-3" />
          <h1 className="text-xl font-semibold text-foreground mb-2">Devis inaccessible</h1>
          <p className="text-sm text-muted-foreground mb-4">
            {error || 'Ce devis est introuvable ou le lien a expiré.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button variant="outline" onClick={() => navigate('/')}>
              Accueil
            </Button>
            <Button variant="default" onClick={() => navigate('/login-authentication')}>
              Se connecter
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-foreground">Votre devis</h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">{identityHint}</p>
          </div>
          {!sessionUser && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate('/login-authentication')}
            >
              Connexion
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <QuoteDetailsPanel
          appointment={appointment}
          quote={quote}
          onRetryQuote={loadQuote}
          onQuoteDecision={handleQuoteDecision}
          onAcceptBooking={handleStartAcceptBooking}
          onContinueBooking={handleStartAcceptBooking}
        />
      </main>
    </div>
  );
};

export default PublicQuotePage;
